<template>
    <v-sheet
        class="d-flex flex-column"
    >
        <v-alert
            v-if="payment_status === 'paid'"
            :text="translate('common.case_paid')"
            class="ma-4"
            type="success"
            variant="tonal"
        />
        <v-alert
            v-else-if="payment_status === 'case_closed'"
            :text="translate('common.case_closed')"
            class="ma-4"
            type="info"
            variant="tonal"
        />
        <v-alert
            v-else-if="payment_status === 'unpaid'"
            :text="translate('common.case_unpaid_text')"
            :title="translate('common.case_unpaid')"
            class="ma-4"
            type="warning"
            variant="tonal"
        />
        <div
            v-if="show_payment_methods"
            class="pa-4"
        >
            <div class="text-h5 bold">
                {{ amount_currency_string }}
            </div>
            <div class="text-subtitle-2">{{ amount_subtitle }}</div>
        </div>
        <div
            v-if="show_payment_methods"
            class="flex-grow-1 d-flex flex-column"
        >
            <v-spacer />
            <div class="mx-4 mb-8">
                <v-fade-transition leave-absolute>
                    <div v-show="!confirmed_mobile && !confirmed_demo_payment">
                        <div class="text-subtitle-1 bold mb-4">{{ translate('common.choose_payment_method') }}</div>
                        <div>
                            <v-card
                                v-for="(provider, index) in payment_providers"
                                :key="index"
                                @click="handle_provider(provider.key)"
                                class="provider d-flex align-center pa-4 mb-4"
                                variant="outlined"
                            >
                                <v-avatar
                                    :image="provider.avatar"
                                    :rounded="0"
                                    :size="64"
                                />
                                <div class="px-4 text-subtitle-1">{{ provider.name }}</div>
                                <v-spacer />
                                <v-icon>mdi-arrow-right</v-icon>
                            </v-card>
                        </div>
                    </div>
                </v-fade-transition>
                <v-fade-transition leave-absolute>
                    <div v-show="confirmed_mobile">
                        <div class="text-subtitle-1 bold mb-4">
                            {{ translate('common.confirm_mobile_number') }}
                        </div>
                        <PhoneField
                            v-model:input="mobile_number"
                            v-model:valid="valid_mobile_number"
                            :preferred_countries="['SE', 'DK', 'NO', 'FI', 'IS']"
                            class="mb-4"
                            invalid_message="common.invalid_mobile_number"
                            label="common.mobile_number"
                        />
                        <v-btn
                            :disabled="!valid_mobile_number"
                            @click="handle_provider('swish')"
                            class="mt-8 mb-4"
                            color="primary"
                            variant="flat"
                            width="100%"
                        >
                            {{ translate('common.pay_with_swish') }}
                        </v-btn>
                    </div>
                </v-fade-transition>
                <v-fade-transition leave-absolute>
                    <div v-show="confirmed_demo_payment">
                        <div class="text-subtitle-1 bold mb-4">
                            {{ translate('common.confirm_demo_payment_amount') }}
                        </div>
                        <TextField
                            v-model:input="demo_payment_amount"
                            v-model:valid="valid_demo_payment_amount"
                            :label="translate('common.amount')"
                            :rules="[(v) => validate_digits(v)]"
                            type="number"
                        />
                        <v-btn
                            :disabled="!valid_demo_payment_amount"
                            @click="$emit('demo', demo_payment_amount)"
                            class="mt-8 mb-4"
                            color="primary"
                            variant="flat"
                            width="100%"
                        >
                            {{ translate('common.pay') }}
                        </v-btn>
                    </div>
                </v-fade-transition>
            </div>
        </div>
    </v-sheet>
</template>

<script>
import { mapState } from 'vuex'
import { locales_mixin } from '@old/mixins/locales_mixin'
import TextField from '@old/components/input/TextField.vue'
import { rules_mixin } from '@old/mixins/rules_mixin'
import { utils_mixin } from '@old/mixins/utils_mixin'
import PhoneField from '@old/components/input/PhoneField.vue'

export default {
    name: 'PaymentProvider',
    props: {
        payment_amount_and_currency: Object,
    },
    mixins: [locales_mixin, rules_mixin, utils_mixin],
    components: { PhoneField, TextField },
    emits: ['mobile_number', 'provider', 'demo'],
    data() {
        return {
            payment_providers: [],
            confirmed_mobile: false,
            mobile_number: '',
            valid_mobile_number: false,
            confirmed_demo_payment: false,
            demo_payment_amount: '',
            valid_demo_payment_amount: false,
        }
    },
    computed: {
        amount_currency_string() {
            const { amount, currency } = this.payment_amount_and_currency
            return this.format_as_currency(amount, currency, 2)
        },
        amount_subtitle() {
            return this.deep_get(this.payment, 'ocr.ocr')
        },
        payment_status() {
            return this.deep_get(this.payment, 'ocr.payment_status')
        },
        show_payment_methods() {
            return this.payment_status !== 'paid' && this.payment_status !== 'case_closed'
        },
        demo_payment_available() {
            return [
                'localhost:8081',
                'ada-customer-test.vfs.visma.com',
                'ada-customer-stage.vfs.visma.com',
                'ada-customer-sandbox.vfs.visma.com',
                'app-test.mittamili.se',
                'app-stage.mittamili.se',
                'app-sandbox.mittamili.se',
            ].includes(location.host)
        },
        ...mapState(['payment', 'user']),
    },
    watch: {},
    methods: {
        get_payment_providers() {
            if (this.payment.ocr.currency === 'SEK' && this.payment.ocr.amount <= 150000) {
                this.payment_providers.push({
                    name: this.translate('common.swish'),
                    key: 'swish',
                    avatar: this.get_avatar('swish'),
                })
            }
            this.payment_providers.push({
                name: this.translate('common.bank_account'),
                key: 'trustly',
                avatar: this.get_avatar('trustly'),
            })
            if (this.demo_payment_available) {
                this.payment_providers.push({
                    name: this.translate('common.demo_payment'),
                    key: 'demo',
                    avatar: this.get_avatar('visma'),
                })
            }
        },
        get_avatar(name) {
            return new URL(`/src/assets/${name}.png`, import.meta.url).href
        },
        handle_provider(key) {
            if (key === 'swish') {
                if (!this.confirmed_mobile) {
                    return (this.confirmed_mobile = true)
                } else {
                    this.$emit('mobile_number', this.mobile_number)
                }
            } else if (key === 'demo') {
                if (!this.confirmed_demo_payment) {
                    return (this.confirmed_demo_payment = true)
                }
            }
            this.$emit('provider', key)
        },
    },
    beforeCreate() {
    },
    created() {
    },
    beforeMount() {
    },
    mounted() {
        this.get_payment_providers()
        const mobile_number = this.deep_get(this.user, 'mobile_number_data.mobile_number')
        if (mobile_number) {
            this.mobile_number = mobile_number
        }
    },
    beforeUpdate() {
    },
    updated() {
    },
    beforeUnmount() {
    },
}
</script>

<style scoped lang="sass">
.provider
    border-color: #E2E2E2
</style>
